import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiError, formatGBP, formatGBPCompact, formatGBP2, businessLoanCost } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { LogOut, ArrowRight, ArrowLeft, TrendingUp, Zap, DollarSign, Send, Check, Copy, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/* -------------------------------------------------------------
 * Lender dashboard — live deal feed
 * ---------------------------------------------------------- */
export default function LenderDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refState, setRefState] = useState(null);

  useEffect(() => {
    if (user && user.role === "lender") {
      (async () => {
        try {
          const [dealsRes, refRes] = await Promise.all([
            api.get("/lender/deals"),
            api.get("/lender/referrals"),
          ]);
          setDeals(dealsRes.data);
          setRefState(refRes.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
      })();
    }
  }, [user]);

  const shareUrl = refState ? `${window.location.origin}/lender/signup?ref=${refState.referral_code}` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Referral link copied to clipboard");
    } catch { toast.error("Couldn't copy — please copy manually."); }
  };

  if (!ready) return <FullscreenLoader label="Loading…" />;
  if (!user || user.role !== "lender") return <Navigate to="/lender/login" replace />;

  return (
    <div className="min-h-screen bg-navy-deep text-white pb-24">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-navy-deep/95 backdrop-blur-xl">
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/lender" className="flex items-center gap-2.5">
            <LogoMark size={38} />
            <div>
              <div className="font-display font-extrabold text-[18px] tracking-[-0.035em]">Swift<span className="text-emerald">Cash</span></div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">Lender</div>
            </div>
          </Link>
          <div className="flex items-center gap-5">
            <span className="hidden md:block font-mono text-[11px] uppercase tracking-[0.15em] text-white/45 font-semibold">{user.email}</span>
            <button onClick={() => { logout(); nav("/"); }} data-testid="lender-logout" className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/70 hover:text-white">
              <LogOut className="h-4 w-4" strokeWidth={2.4} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-5 md:px-8 py-10 md:py-14 space-y-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-4">
            Live deals
          </div>
          <h1 className="font-display font-extrabold text-[44px] md:text-[60px] leading-[0.95] tracking-[-0.04em]">
            {deals.length} live {deals.length === 1 ? "deal" : "deals"} on the panel
          </h1>
        </div>

        {loading && <div className="text-white/50 font-semibold">Loading deals…</div>}
        {!loading && deals.length === 0 && (
          <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-14 text-center">
            <div className="text-white/60 font-semibold text-[15px]">No live deals right now. Fresh applications appear throughout the day.</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {deals.map((d) => (
            <button key={d.id} onClick={() => nav(`/lender/deals/${d.id}`)} data-testid={`deal-card-${d.id}`}
              className="text-left rounded-[20px] border border-white/10 bg-white/[0.02] p-6 hover:border-emerald/40 hover:bg-white/[0.04] transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] font-bold text-white/50 mb-1">Amount · Term</div>
                  <div className="font-display font-extrabold text-[26px] tracking-[-0.02em]">{formatGBPCompact(d.amount)} <span className="text-white/50 text-[16px]">· {d.term_months}m</span></div>
                </div>
                <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-emerald group-hover:translate-x-0.5 transition-all" strokeWidth={2.4} />
              </div>
              <div className="space-y-2 text-[13px]">
                <Row label="Purpose" value={d.purpose} />
                <Row label="Sector" value={d.sector} />
                <Row label="Trading" value={d.trading_years} />
                <Row label="Turnover" value={d.turnover_band} />
                <Row label="Existing debt" value={d.existing_debt_band} />
                <Row label="Postcode" value={d.business_postcode} />
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 font-mono text-[10.5px] uppercase tracking-[0.12em] text-white/45 font-bold">
                Ref · {d.id.slice(0, 8)}
              </div>
            </button>
          ))}
        </div>

        {/* Referrals card */}
        {refState && (
          <div className="rounded-[20px] border border-emerald/20 bg-emerald/[0.04] p-6 md:p-8" data-testid="lender-referrals-card">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold mb-3">
                  <Users className="h-3 w-3" strokeWidth={2.6} /> Panel referrals & rewards
                </div>
                <h2 className="font-display font-extrabold text-[26px] md:text-[32px] tracking-[-0.03em] leading-tight mb-2">
                  Refer a fellow funder. <span className="text-emerald">Unlock rewards.</span>
                </h2>
                {refState.current_tier ? (
                  <p className="text-[14px] text-white/80 font-medium leading-[1.55] max-w-lg">
                    You&apos;re on tier <strong className="text-emerald">{refState.current_tier.label}</strong> — {refState.current_tier.perk}
                  </p>
                ) : (
                  <p className="text-[14px] text-white/70 font-medium leading-[1.55] max-w-lg">
                    Refer 1 approved lender to unlock <strong className="text-emerald">5% off your success fee</strong> — and it scales up from there.
                  </p>
                )}
                {refState.next_tier && (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[11.5px] font-semibold text-white/70">
                    <span className="font-mono uppercase tracking-[0.1em] text-white/50">Next</span>
                    <span className="text-white">{refState.next_tier.label}</span>
                    <span className="text-white/50">·</span>
                    <span className="text-white/70">{refState.next_tier.threshold - refState.approved_count} more to go</span>
                  </div>
                )}
              </div>
              <div className="w-full md:w-auto md:min-w-[380px]">
                <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4 mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] font-bold text-white/45 mb-2">Your referral link</div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={shareUrl} data-testid="referral-link-input"
                      className="flex-1 min-w-0 bg-transparent text-[12.5px] text-white font-mono truncate outline-none" />
                    <button onClick={copyLink} data-testid="referral-copy-btn"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald text-white text-[12px] font-extrabold hover:bg-emerald-2 shrink-0">
                      <Copy className="h-3.5 w-3.5" strokeWidth={2.4} /> Copy
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <RefStat label="Total" value={refState.total} />
                  <RefStat label="Approved" value={refState.counts.approved || 0} />
                  <RefStat label="Pending" value={refState.counts.pending || 0} />
                </div>
              </div>
            </div>

            {/* Tier ladder */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-6 border-t border-white/10">
              {refState.tiers.map((t) => {
                const unlocked = refState.approved_count >= t.threshold;
                const isCurrent = refState.current_tier?.threshold === t.threshold;
                return (
                  <div key={t.threshold} data-testid={`tier-${t.threshold}`}
                    className={`rounded-lg p-3 border transition-all ${
                      isCurrent ? "bg-emerald/15 border-emerald/50" :
                      unlocked ? "bg-white/[0.03] border-emerald/25" :
                      "bg-white/[0.02] border-white/10 opacity-60"
                    }`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] font-extrabold text-white/50">{t.threshold} refs</span>
                      {unlocked && <Check className="h-3 w-3 text-emerald" strokeWidth={3} />}
                    </div>
                    <div className="font-display font-extrabold text-[13px] tracking-[-0.015em] mb-1">{t.label}</div>
                    <div className="text-[11px] text-white/65 leading-[1.4] font-medium">{t.perk}</div>
                  </div>
                );
              })}
            </div>

            {refState.referrals.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] font-bold text-white/45 mb-3">Your referrals</div>
                <div className="space-y-2">
                  {refState.referrals.map((r) => (
                    <div key={r.id} data-testid={`referral-row-${r.id}`}
                      className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-white/10 px-4 py-3">
                      <div className="min-w-0">
                        <div className="text-[13.5px] text-white font-bold truncate">{r.company}</div>
                        <div className="text-[11.5px] text-white/45 font-mono">{r.name}</div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-mono font-extrabold uppercase tracking-[0.1em]
                        ${r.status === "approved" ? "bg-emerald text-white" :
                          r.status === "pending" ? "bg-white/10 text-white/70 border border-white/15" :
                          "bg-coral/15 text-coral border border-coral/30"}`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold text-white/45">{label}</span>
      <span className="text-white/85 font-semibold text-right truncate ml-3">{value || "—"}</span>
    </div>
  );
}
function RefStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-center">
      <div className="font-display font-extrabold text-[20px] tracking-[-0.02em] leading-none">{value}</div>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-white/45 mt-1 font-bold">{label}</div>
    </div>
  );
}

/* -------------------------------------------------------------
 * Lender deal detail — bid + live leaderboard
 * ---------------------------------------------------------- */
export function LenderDealDetail() {
  const { id } = useParams();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bid, setBid] = useState({ apr: "", arrangement_fee_pct: "", monthly_fee: "0", terms_note: "", expires_hours: 72 });

  useEffect(() => {
    if (user && user.role === "lender") load();
    // eslint-disable-next-line
  }, [user, id]);

  useEffect(() => {
    if (!user || user.role !== "lender") return;
    const token = localStorage.getItem("sc_token");
    if (!token) return;
    const wsBase = API.replace(/^http/, "ws").replace(/\/api$/, "");
    const ws = new WebSocket(`${wsBase}/api/ws/deal/${id}?token=${token}`);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "bid_update") load();
      } catch { /* ignore malformed messages */ }
    };
    return () => { try { ws.close(); } catch { /* already closed */ } };
    // eslint-disable-next-line
  }, [user, id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/lender/deals/${id}`);
      setState(data);
      if (data.your_bid) {
        setBid({
          apr: String(data.your_bid.apr),
          arrangement_fee_pct: String(data.your_bid.arrangement_fee_pct),
          monthly_fee: String(data.your_bid.monthly_fee),
          terms_note: data.your_bid.terms_note || "",
          expires_hours: 72,
        });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (!bid.apr) return toast.error("Enter an APR to submit.");
    setBusy(true);
    try {
      await api.post(`/lender/deals/${id}/bid`, {
        apr: Number(bid.apr),
        arrangement_fee_pct: Number(bid.arrangement_fee_pct || 0),
        monthly_fee: Number(bid.monthly_fee || 0),
        terms_note: bid.terms_note,
        expires_hours: Number(bid.expires_hours || 72),
      });
      toast.success("Bid submitted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!ready) return <FullscreenLoader label="Loading…" />;
  if (!user || user.role !== "lender") return <Navigate to="/lender/login" replace />;
  if (!state) return <FullscreenLoader label="Loading deal…" />;

  const d = state.deal;
  const { monthly } = businessLoanCost(d.amount, d.term_months, (Number(bid.apr) || 8.9) / 100);
  const yourAPR = state.your_bid?.apr;

  return (
    <div className="min-h-screen bg-navy-deep text-white pb-24">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-navy-deep/95 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-5">
            <button onClick={() => nav("/lender")} className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/70 hover:text-white" data-testid="lender-back">
              <ArrowLeft className="h-4 w-4" strokeWidth={2.4} /> All deals
            </button>
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/50 font-bold">Ref · {d.id.slice(0, 8)}</div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-10 md:py-14 space-y-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-4">
            Live deal card
          </div>
          <h1 className="font-display font-extrabold text-[52px] md:text-[72px] leading-[0.95] tracking-[-0.04em]">
            {formatGBPCompact(d.amount)} <span className="text-emerald">/ {d.term_months}m</span>
          </h1>
          <p className="mt-3 font-mono text-[12px] uppercase tracking-[0.12em] text-white/45 font-semibold">
            {d.purpose} · {d.sector} · {d.trading_years}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 rounded-[20px] border border-white/10 bg-white/[0.02] p-6 space-y-4">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45">Redacted profile</div>
            <KV label="Turnover" value={d.turnover_band} />
            <KV label="Existing debt" value={d.existing_debt_band} />
            <KV label="Postcode" value={d.business_postcode} />
            <KV label="Trading years" value={d.trading_years} />
            <KV label="Sector" value={d.sector} />
          </div>

          <div className="lg:col-span-2 rounded-[20px] border border-emerald/25 bg-emerald/[0.04] p-6 md:p-7">
            <div className="flex items-center justify-between mb-5">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-emerald">Your bid</div>
              {yourAPR != null && <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/60 font-bold">Live · {yourAPR.toFixed(2)}%</div>}
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-5">
              <BidField label="APR %" v={bid.apr} onChange={(v) => setBid({ ...bid, apr: v })} testid="bid-apr" />
              <BidField label="Arrangement %" v={bid.arrangement_fee_pct} onChange={(v) => setBid({ ...bid, arrangement_fee_pct: v })} testid="bid-arrangement" />
              <BidField label="Monthly fee £" v={bid.monthly_fee} onChange={(v) => setBid({ ...bid, monthly_fee: v })} testid="bid-monthly" />
            </div>

            <div className="mb-5">
              <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/60 mb-2 block">Terms note</Label>
              <Textarea rows={3} value={bid.terms_note} onChange={(e) => setBid({ ...bid, terms_note: e.target.value })}
                data-testid="bid-note"
                placeholder="Security, PG, conditions precedent, sector caveats…"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-emerald focus-visible:border-emerald font-medium" />
            </div>

            <div className="grid md:grid-cols-2 gap-4 items-center">
              <div className="rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.12em] font-bold text-white/45 mb-1">Applicant monthly (est.)</div>
                <div className="font-display font-extrabold text-[19px] tracking-[-0.02em]">{formatGBP2(monthly)}</div>
              </div>
              <button onClick={submit} disabled={busy} data-testid="bid-submit"
                className="btn-shine group inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald text-white font-extrabold hover:bg-emerald-2 text-[14px] disabled:opacity-50 shadow-emerald">
                <Send className="h-4 w-4" strokeWidth={2.4} /> {state.your_bid ? "Update bid" : "Submit bid"}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[20px] border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-6 py-4 bg-white/[0.03] border-b border-white/10 flex items-center justify-between">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald pulse-dot" /> Live leaderboard · {state.leaderboard.length} bid{state.leaderboard.length === 1 ? "" : "s"}
            </div>
            <div className="font-mono text-[10.5px] text-white/40 font-bold">Ranked by APR</div>
          </div>
          {state.leaderboard.length === 0 && (
            <div className="p-10 text-center text-white/50 font-semibold">No bids yet. Be the first.</div>
          )}
          {state.leaderboard.map((b, i) => (
            <div key={i} data-testid={`leaderboard-row-${i}`}
              className={`grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 last:border-0 items-center
                ${b.is_yours ? "bg-emerald/[0.08]" : ""}`}>
              <div className="col-span-1 font-display font-extrabold text-[20px] tracking-[-0.02em] text-white/60">#{i + 1}</div>
              <div className="col-span-3 font-display font-extrabold text-[22px] tracking-[-0.02em]">
                {b.apr.toFixed(2)}<span className="text-white/40 text-[14px] ml-0.5">%</span>
              </div>
              <div className="col-span-3 font-mono text-[12px] text-white/60 font-bold">{b.arrangement_fee_pct.toFixed(2)}% arr.</div>
              <div className="col-span-3 font-mono text-[12px] text-white/60 font-bold">{formatGBP2(b.monthly_fee)} /mo</div>
              <div className="col-span-2 text-right">
                {b.is_yours ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald text-white text-[10px] font-mono font-extrabold uppercase tracking-[0.1em]">
                    <Check className="h-3 w-3" strokeWidth={3} /> Yours
                  </span>
                ) : (
                  <span className="font-mono text-[10.5px] text-white/40 font-bold">Competing</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function BidField({ label, v, onChange, testid }) {
  return (
    <div>
      <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/60 mb-2 block">{label}</Label>
      <Input type="number" step="0.01" min="0" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="h-12 rounded-xl bg-white/5 border-white/15 text-white placeholder:text-white/30 font-display font-extrabold text-[18px] tracking-[-0.02em] focus-visible:ring-emerald focus-visible:border-emerald" />
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/50">{label}</span>
      <span className="font-display font-extrabold text-[15px] text-white tracking-[-0.02em]">{value || "—"}</span>
    </div>
  );
}
function FullscreenLoader({ label }) {
  return <div className="min-h-screen bg-navy-deep text-white/50 flex items-center justify-center">{label}</div>;
}
